import React, { useEffect, useRef, useState } from "react";

import { Modal, Button, Form, Input, Checkbox } from "antd";
import Parse from "parse";
import { toast } from "react-toastify";

import GenerateUserRecoveryKit, { UserRecoveryKitRef } from "./onboarding/GenerateUserRecoveryKit";
import DfnsService from "../services/DfnsService";
import {
  formatSecret,
  keepSecretCaret,
  passkeyErrorMessage,
  RECOVERY_SECRET_PATTERN,
  RECOVERY_SECRET_PLACEHOLDER,
  skipSecretHyphen,
} from "../utils/dfnsRecoveryKey";

interface RecoverDfnsKeyModalProps {
  visible: boolean;
  onClose: () => void;
}

const RecoverDfnsKeyModal: React.FC<RecoverDfnsKeyModalProps> = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const userRecoveryKitRef = useRef<UserRecoveryKitRef | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [newCredentialId, setNewCredentialId] = useState<string | null>(null);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasPrintedAck, setHasPrintedAck] = useState(false);

  useEffect(() => {
    if (visible) {
      const currentUser = Parse.User.current();
      setUsername(currentUser ? currentUser.get("username") : null);
    }
  }, [visible]);

  const handleRecoveryProcess = async (data: { credentialId: string; secret: string }) => {
    try {
      setLoading(true);

      const currentUser = Parse.User.current();
      if (!currentUser) {
        toast.error("No user logged in");
        setLoading(false);
        return;
      }

      const fetchedUsername = currentUser.get("username");
      setUsername(fetchedUsername);

      // Strip ALL whitespace from the Credential ID, not just the ends: the recovery-kit
      // PDF soft-wraps this long base64url token, and copying it back out turns the wrap
      // point into a space mid-string. The Secret is already cleaned by formatSecret.
      const storedRecoveryKey = {
        credentialId: data.credentialId?.replace(/\s+/g, ""),
        secret: data.secret,
      };

      const {
        recoveryChallenge,
        tempAuthToken,
        recoveryKey,
        error: initError,
      } = await DfnsService.initiateRecovery(fetchedUsername, storedRecoveryKey);

      if (initError) {
        toast.error(passkeyErrorMessage(undefined, initError, "We couldn't start passkey recovery. Please try again."));
        setLoading(false);
        return;
      }

      const {
        newStoredRecoveryKey,
        error: completeError,
        errorName,
      } = await DfnsService.completeRecovery(
        {
          ...recoveryChallenge,
          username: fetchedUsername,
          tempAuthToken,
          recoveryKey,
        },
        storedRecoveryKey
      );

      // Judge by the result, not the error text: a failed decrypt can come back with an empty message.
      if (!newStoredRecoveryKey) {
        toast.error(passkeyErrorMessage(errorName, completeError, "We couldn't complete passkey recovery. Please try again."));
      } else {
        form.resetFields();
        setNewCredentialId(newStoredRecoveryKey?.credentialId || null);
        setNewSecret(newStoredRecoveryKey?.secret || null);
        setHasPrintedAck(false);
        toast.success("Passkey recovered successfully! Your wallet is ready to use.");
      }
    } catch (error: any) {
      toast.error(error?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Also guards the modal's own X icon / Escape key, not just the Close
    // button, so there's no way to dismiss without acknowledging once a
    // recovery kit has been generated.
    if (newCredentialId && !hasPrintedAck) {
      toast.error("Please confirm you've printed and saved the recovery kit before closing.");
      return;
    }
    form.resetFields();
    setUsername(null);
    setNewCredentialId(null);
    setNewSecret(null);
    setHasPrintedAck(false);
    onClose();
  };

  const handleDownloadRecoveryFile = () => {
    if (userRecoveryKitRef.current) {
      userRecoveryKitRef.current.generatePDF().catch((error: any) => toast.error(error?.message || "Failed to generate recovery kit"));
    }
  };

  return (
    <Modal title="Recover Dfns Passkey" open={visible} onOk={onClose} onCancel={handleCancel} footer={null} maskClosable={false}>
      <Form layout="vertical" form={form} onFinish={handleRecoveryProcess}>
        <Form.Item
          name="credentialId"
          label="Credential Id (Recovery Code)"
          rules={[{ required: true, message: "Please enter Credential Id (Recovery Code)" }]}
        >
          <Input placeholder="Credential Id (Recovery Code)" />
        </Form.Item>
        <Form.Item
          name="secret"
          label="Secret (Recovery Key ID)"
          rules={[
            { required: true, message: "Please enter Secret (Recovery Key ID)" },
            {
              pattern: RECOVERY_SECRET_PATTERN,
              message: "Secret format is invalid. It must be 7 groups of capital letters and digits (2-6-5-5-5-5-6), separated by hyphens.",
              // Only on blur and submit, so a valid Secret isn't shown as invalid while it's still being typed.
              validateTrigger: "onBlur",
            },
          ]}
          validateTrigger={["onChange", "onBlur"]}
          normalize={formatSecret}
          extra="Format: 2-6-5-5-5-5-6 characters, capital letters and digits, separated by hyphens."
        >
          <Input
            placeholder={RECOVERY_SECRET_PLACEHOLDER}
            className="font-mono"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            onChange={keepSecretCaret}
            onKeyDown={skipSecretHyphen}
          />
        </Form.Item>

        <Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={handleCancel} disabled={!!newCredentialId && !hasPrintedAck}>
              Close
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Recover
            </Button>
          </div>
        </Form.Item>
      </Form>

      {newCredentialId && (
        <>
          <p className="text-gray-500 text-base mt-6">Your recovery file has been generated. Please print and store it in a safe place.</p>
          <GenerateUserRecoveryKit ref={userRecoveryKitRef} username={username || ""} credentialId={newCredentialId || ""} secret={newSecret || ""} />
          <Checkbox checked={hasPrintedAck} onChange={(e) => setHasPrintedAck(e.target.checked)} className="mt-4">
            I have printed and saved this recovery kit.
          </Checkbox>
          <br />
          <button onClick={handleDownloadRecoveryFile} className="bg-blue-500 text-white px-4 py-2 mt-4 rounded-md">
            Print
          </button>
        </>
      )}
    </Modal>
  );
};

export default RecoverDfnsKeyModal;
